import { capitalize } from '@/domain/common/format'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { assets } from '@/ui/assets'
import { Tooltip, TooltipContentLong, TooltipTrigger } from '@/ui/atoms/tooltip/Tooltip'
import { TooltipContentLayout } from '@/ui/atoms/tooltip/TooltipContentLayout'
import { IsolatedPartnerPointsPerPair } from '@/config/chain/types'
import Image from 'next/image'
import { cn } from '@/ui/utils/style'

type Action = 'deposit' | 'borrow' | 'lend' | 'lp-deposit'

type BasePointsBadgeProps = {
  'data-testid'?: string
  action: Action
  multiplier?: number
  partnerPoints?: IsolatedPartnerPointsPerPair[]
  extraIncentives?: string
  additionalAPY?: string
  baseAPY?: string
  totalAPY?: string
  size?: 'sm' | 'md' | 'lg'
}

type NonLpProps = BasePointsBadgeProps & {
  action: Exclude<Action, 'lp-deposit'>
  asset: TokenSymbol
  poolName?: never
}

type LpProps = BasePointsBadgeProps & {
  action: 'lp-deposit'
  poolName: string
  asset?: never
}

type PointsBadgeProps = NonLpProps | LpProps

export function PointsBadge(props: PointsBadgeProps) {
  const {
    action,
    multiplier,
    partnerPoints,
    'data-testid': dataTestId,
    extraIncentives,
    additionalAPY,
    baseAPY,
    totalAPY,
    size = 'sm',
  } = props
  const subject = action === 'lp-deposit' ? props.poolName : props.asset
  const partnerConfig = partnerPoints?.[0]
  const extraIncentivesText = extraIncentives?.split('<link>')[0]
  const extraIncentivesLink = extraIncentives?.split('<link>')[1]

  return (
    <Tooltip>
      <TooltipTrigger asChild onClick={(e) => e.stopPropagation()}>
        <div data-testid={dataTestId}>
          <div className="shrink-0">
            <div className="flex items-center gap-1 rounded">
              {extraIncentives && (
                <span className="flex items-center gap-1 rounded border border-white/20 px-1 py-0.5">
                  <Image src={assets.incentivizedBadge} alt="Incentivized" className="h-3.5 w-3.5" />
                </span>
              )}
              {multiplier && (
                <span
                  className={cn('flex items-center gap-1 rounded border border-white/20 px-1 py-0.5', {
                    'px-1 py-0.5': size === 'sm',
                    'px-2 py-1': size === 'md',
                    'gap-2 px-2 py-1': size === 'lg',
                  })}
                >
                  <span className="text-muted-foreground text-xs">{multiplier}x</span>
                  <Image
                    src={assets.hypurrPawNoShadow}
                    alt={'HypurrFi Points'}
                    className={cn({
                      'h-3.5 w-3.5': size === 'sm' || size === 'md',
                      'h-4 w-4': size === 'lg',
                    })}
                    aria-label={'HypurrFi Points'}
                  />
                </span>
              )}

              {partnerConfig?.[action] && (
                <span className="flex items-center gap-1 rounded border border-white/20 px-1 py-0.5">
                  {!!partnerConfig?.[action].multiplier && (
                    <span className="text-muted-foreground text-xs">{partnerConfig[action]?.multiplier}x</span>
                  )}
                  <Image
                    src={assets[partnerConfig.icon as keyof typeof assets]}
                    alt={partnerConfig.title}
                    className="h-3.5 w-3.5"
                    aria-label={'HypurrFi Points'}
                  />
                </span>
              )}
            </div>
          </div>
        </div>
      </TooltipTrigger>

      <TooltipContentLong forceMount>
        <TooltipContentLayout>
          {multiplier && (
            <>
              <TooltipContentLayout.Header>
                <TooltipContentLayout.Icon src={assets.hypurrPawNoShadow} />
                <TooltipContentLayout.Title>{getActionTitle(action)} eligible for HP</TooltipContentLayout.Title>
              </TooltipContentLayout.Header>

              <TooltipContentLayout.Body>
                <span className="text-sm">{getActionText(action, subject)}</span>
              </TooltipContentLayout.Body>
            </>
          )}

          {partnerConfig?.[action] && (
            <>
              <TooltipContentLayout.Header>
                <TooltipContentLayout.Icon src={assets[partnerConfig.icon as keyof typeof assets]} />
                <TooltipContentLayout.Title>{partnerConfig.title}</TooltipContentLayout.Title>
              </TooltipContentLayout.Header>

              <TooltipContentLayout.Body>
                <span className="text-sm">{getPartnerActionText(action, subject, partnerConfig.tooltipBody)}</span>
              </TooltipContentLayout.Body>
            </>
          )}

          {extraIncentives && (
            <>
              <TooltipContentLayout.Header>
                <TooltipContentLayout.Icon src={assets.incentivizedBadge} />
                <TooltipContentLayout.Title>Extra Incentives</TooltipContentLayout.Title>
              </TooltipContentLayout.Header>

              <TooltipContentLayout.Body>
                <span className="text-sm">{extraIncentivesText}</span>
                {(baseAPY || additionalAPY || totalAPY) && (
                  <span className="mt-2 flex flex-col gap-1 text-muted-foreground text-xs leading-tight">
                    {baseAPY && <span>Base: {baseAPY}</span>}
                    {additionalAPY && <span>Bonus: {additionalAPY}</span>}
                    {totalAPY && <span>Total: {totalAPY}</span>}
                  </span>
                )}
                {extraIncentivesLink && (
                  <a
                    href={extraIncentivesLink}
                    className="pl-1 text-primary text-sm underline"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Claim rewards
                  </a>
                )}
              </TooltipContentLayout.Body>
            </>
          )}
        </TooltipContentLayout>
      </TooltipContentLong>
    </Tooltip>
  )
}

function getActionTitle(action: Action) {
  switch (action) {
    case 'lp-deposit':
      return 'LP Deposit'
    default:
      return capitalize(action)
  }
}

function getActionText(action: Action, subject: string) {
  switch (action) {
    case 'lp-deposit':
      return `Holders of ${subject} LP are eligible for Hypurrfi Points`
    default:
      return `HypurrFi users who ${action} ${subject} are eligible for HypurrFi Points (HP).`
  }
}

function getPartnerActionText(action: Action, subject: string, tooltipBody: string) {
  switch (action) {
    case 'lp-deposit':
      return `Holders of ${subject} ${tooltipBody}`
    default:
      return `HypurrFi users who ${action} ${subject} ${tooltipBody}`
  }
}
